/**
 * "Use previous tags" — the remembered tag set carried from one record capture
 * to the next. See [[image-viewer-modal.spec]] ("Previous tags on create").
 *
 * Scoped to **one user in one project**, and that scoping is the whole
 * correctness story: on a shared site device the next person to sign in must
 * never inherit the previous person's tags. The user id is part of the key, and
 * callers must not invoke these helpers until they have a real one — there is
 * deliberately no "anonymous" fallback key to fall into.
 *
 * localStorage rather than IndexedDB because the record form seeds its default
 * values synchronously (`createDefaultValues`); an async read would make
 * pre-filled tags pop in a frame late. Follows the house pattern in
 * `precon-draft.ts`: every access wrapped, malformed data treated as absent,
 * writes best-effort (private mode / quota exhaustion degrades to "no memory",
 * never to a crash mid-capture).
 */

export interface PreviousTagsEntry {
  /** Whether the user has the toggle switched on for this project. */
  enabled: boolean;
  /** Tag ids from the last record this user created in this project. */
  tagIds: string[];
  updatedAt: number;
}

const key = (projectId: string, userId: string) =>
  `otus:previous-tags:${projectId}:${userId}`;

export function readPreviousTags(
  projectId: string,
  userId: string,
): PreviousTagsEntry | null {
  try {
    const raw = localStorage.getItem(key(projectId, userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const entry = parsed as Partial<PreviousTagsEntry>;
    // Validate rather than trust: a half-written or hand-edited row must read
    // as "no memory", not poison the form with `undefined` tag ids.
    if (!Array.isArray(entry.tagIds)) return null;
    if (!entry.tagIds.every((id) => typeof id === "string")) return null;
    return {
      enabled: entry.enabled === true,
      tagIds: entry.tagIds,
      updatedAt: typeof entry.updatedAt === "number" ? entry.updatedAt : 0,
    };
  } catch {
    return null;
  }
}

export function writePreviousTags(
  projectId: string,
  userId: string,
  entry: Omit<PreviousTagsEntry, "updatedAt">,
): void {
  try {
    localStorage.setItem(
      key(projectId, userId),
      JSON.stringify({ ...entry, updatedAt: Date.now() }),
    );
  } catch {
    /* storage unavailable — the feature degrades to session-only */
  }
}
